import maplibregl, { type Map as MlMap, type GeoJSONSource } from "maplibre-gl";
import { RIVERS, THEME } from "./config";
import { setUserPin, setUserPinPlace, clearUserPin, tickUserPin, type PinKind } from "./user-pin";
import { pathReachPoints } from "./sim-client";
import { displayRiverName } from "./river-names";

/** Route highlight + radius circle + start pin. */
const ROUTE_LAYER = "route-highlight";
const ROUTE_GLOW = "route-glow";
const RADIUS_SRC = "radius-src";
const RADIUS_FILL = "radius-fill";
const RADIUS_LINE = "radius-line";
const ROUTE_NAMES_SRC = "route-names-src";
const ROUTE_NAMES = "route-names";
const PIN_BLOCK_SRC = "user-pin-block-src";
const PIN_BLOCK = "user-pin-block";
let animTimer: number | null = null;

/** Set the text label shown beside the start pin (e.g. the place name). */
export function setStartLabel(text: string): void {
  setUserPinPlace(text); // shown when the user taps the pin (v0.9.5c)
}

export type FlowStatus = "normal" | "high" | "veryhigh" | "unknown";

const MASK_SRC = "radius-mask-src";
const MASK_FILL = "radius-mask-fill";

/** Dim everything OUTSIDE the radius (donut mask), so the flood/gauges read as
 *  "revealed" inside the circle. Placed above GISTDA but below the basemap labels
 *  (roads/names stay visible). Updated live while dragging the radius. (item 8) */
export function setRadiusMask(map: MlMap, center: { lon: number; lat: number } | null, km: number, segments = 64): void {
  const src = map.getSource(MASK_SRC) as GeoJSONSource | undefined;
  if (!src) return;
  if (!center) { src.setData(emptyFC()); return; }
  const world: [number, number][] = [[-179.9, -85], [179.9, -85], [179.9, 85], [-179.9, 85], [-179.9, -85]];
  const hole = circleRing(center.lon, center.lat, km, segments);
  src.setData({ type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [world, hole] } }] });
}
export function clearRadiusMask(map: MlMap): void { const s = map.getSource(MASK_SRC) as GeoJSONSource | undefined; if (s) s.setData(emptyFC()); }

export function addRouteLayer(map: MlMap): void {
  // dim-outside mask — below the basemap labels (first symbol layer), above GISTDA
  map.addSource(MASK_SRC, { type: "geojson", data: emptyFC() });
  let firstSymbol: string | undefined;
  for (const l of map.getStyle().layers ?? []) if (l.type === "symbol") { firstSymbol = l.id; break; }
  map.addLayer({ id: MASK_FILL, type: "fill", source: MASK_SRC, paint: { "fill-color": "#05080f", "fill-opacity": 0.55, "fill-antialias": false } }, firstSymbol);

  // radius circle (below rivers)
  map.addSource(RADIUS_SRC, { type: "geojson", data: emptyFC() });
  const beforeRivers = map.getLayer("rivers-glow") ? "rivers-glow" : undefined;
  map.addLayer(
    { id: RADIUS_FILL, type: "fill", source: RADIUS_SRC, paint: { "fill-color": "#ffffff", "fill-opacity": 0.05 } },
    beforeRivers,
  );
  map.addLayer(
    {
      id: RADIUS_LINE,
      type: "line",
      source: RADIUS_SRC,
      paint: { "line-color": "#ffffff", "line-width": 1.5, "line-opacity": 0.6, "line-dasharray": [2, 2] },
    },
    beforeRivers,
  );

  // route is drawn ON TOP of everything (added last, no beforeId)
  const emptyFilter: maplibregl.FilterSpecification = ["in", ["get", "id"], ["literal", []]];
  map.addLayer({
    id: ROUTE_GLOW,
    type: "line",
    source: "rivers",
    "source-layer": RIVERS.sourceLayer,
    filter: emptyFilter,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": THEME.magenta, "line-width": 8, "line-opacity": 0.35, "line-blur": 6 },
  });
  map.addLayer({
    id: ROUTE_LAYER,
    type: "line",
    source: "rivers",
    "source-layer": RIVERS.sourceLayer,
    filter: emptyFilter,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": "#ff2bd6", "line-width": 3, "line-dasharray": [0, 2, 4] },
  });

  // names of the rivers/canals ON the user's route (v0.9.5c) — above the general river labels so
  // they're placed first; only a dam / staff / gauge symbol (layers added later) can hide one.
  map.addSource(ROUTE_NAMES_SRC, { type: "geojson", data: emptyFC() });
  map.addLayer({
    id: ROUTE_NAMES, type: "symbol", source: ROUTE_NAMES_SRC,
    layout: {
      "symbol-placement": "line", "text-field": ["get", "name"], "text-font": ["Noto Sans Regular"],
      "text-size": 13, "text-max-angle": 60, "symbol-spacing": 150, "text-padding": 4, "text-allow-overlap": false,
    },
    paint: { "text-color": "#ffd6f6", "text-halo-color": "#05080f", "text-halo-width": 2 },
  });

  // the 📍 is a DOM marker, invisible to MapLibre's label collision → an invisible 28×40 box at the
  // same point (always placed, ignore-placement false) so river / canal names hide instead of
  // printing under the pin (v0.9.5c)
  if (!map.hasImage("user-pin-block")) map.addImage("user-pin-block", { width: 28, height: 40, data: new Uint8Array(28 * 40 * 4) });
  map.addSource(PIN_BLOCK_SRC, { type: "geojson", data: emptyFC() });
  map.addLayer({
    id: PIN_BLOCK, type: "symbol", source: PIN_BLOCK_SRC,
    layout: { "icon-image": "user-pin-block", "icon-anchor": "bottom", "icon-allow-overlap": true, "icon-ignore-placement": false },
    paint: { "icon-opacity": 0 },
  });

  // dam downstream flow (cyan) — glows when a dam is tapped (below the user route)
  map.addLayer({
    id: DAM_FLOW_GLOW, type: "line", source: "rivers", "source-layer": RIVERS.sourceLayer, filter: emptyFilter,
    layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#00e5ff", "line-width": 7, "line-opacity": 0.3, "line-blur": 5 },
  }, ROUTE_GLOW);
  map.addLayer({
    id: DAM_FLOW, type: "line", source: "rivers", "source-layer": RIVERS.sourceLayer, filter: emptyFilter,
    layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#00e5ff", "line-width": 2.6 },
  }, ROUTE_GLOW);
}

const DAM_FLOW = "dam-flow";
const DAM_FLOW_GLOW = "dam-flow-glow";
/** Highlight a dam's downstream path (dashed when there is no discharge). */
export function setDamFlow(map: MlMap, pathIds: number[], dashed: boolean): void {
  const filter: maplibregl.FilterSpecification = ["in", ["get", "id"], ["literal", pathIds]];
  for (const id of [DAM_FLOW, DAM_FLOW_GLOW]) if (map.getLayer(id)) map.setFilter(id, filter);
  if (map.getLayer(DAM_FLOW)) map.setPaintProperty(DAM_FLOW, "line-dasharray", dashed ? [2, 2] : [1]);
}
export function clearDamFlow(map: MlMap): void {
  const empty: maplibregl.FilterSpecification = ["in", ["get", "id"], ["literal", []]];
  for (const id of [DAM_FLOW, DAM_FLOW_GLOW]) if (map.getLayer(id)) map.setFilter(id, empty);
}

export function setRoute(
  map: MlMap,
  pathIds: number[],
  start: { lon: number; lat: number },
  status: FlowStatus = "unknown",
  pinKind: PinKind = "picked",
): void {
  // The route line is the *flow path from the selected point* — an informational
  // cyan line, NOT a danger indicator (status is shown in the card). (C11)
  void status;
  const filter: maplibregl.FilterSpecification = ["in", ["get", "id"], ["literal", pathIds]];
  for (const id of [ROUTE_GLOW, ROUTE_LAYER]) {
    if (map.getLayer(id)) map.setFilter(id, filter);
  }
  if (map.getLayer(ROUTE_LAYER)) map.setPaintProperty(ROUTE_LAYER, "line-color", "#ff2bd6");
  if (map.getLayer(ROUTE_GLOW)) map.setPaintProperty(ROUTE_GLOW, "line-color", "#ff2bd6");
  setUserPin(map, start, pinKind); // 📍 tip on the exact point (replaces the old start-pin)
  const names = map.getSource(ROUTE_NAMES_SRC) as GeoJSONSource | undefined;
  if (names) names.setData(routeNameLines(pathIds));
  (map.getSource(PIN_BLOCK_SRC) as GeoJSONSource | undefined)?.setData({ type: "FeatureCollection",
    features: [{ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: [start.lon, start.lat] } }] });
  animateFlow(map);
}

export function setRadius(map: MlMap, center: { lon: number; lat: number }, km: number): void {
  const src = map.getSource(RADIUS_SRC) as GeoJSONSource | undefined;
  if (src) src.setData(circle(center.lon, center.lat, km));
}

export function clearRoute(map: MlMap): void {
  const empty: maplibregl.FilterSpecification = ["in", ["get", "id"], ["literal", []]];
  for (const id of [ROUTE_GLOW, ROUTE_LAYER]) {
    if (map.getLayer(id)) map.setFilter(id, empty);
  }
  const src = map.getSource(RADIUS_SRC) as GeoJSONSource | undefined;
  if (src) src.setData(emptyFC());
  clearUserPin();
  (map.getSource(ROUTE_NAMES_SRC) as GeoJSONSource | undefined)?.setData(emptyFC());
  (map.getSource(PIN_BLOCK_SRC) as GeoJSONSource | undefined)?.setData(emptyFC());
  if (animTimer !== null) { cancelAnimationFrame(animTimer); animTimer = null; }
}

function animateFlow(map: MlMap): void {
  if (animTimer !== null) cancelAnimationFrame(animTimer);
  const seq: number[][] = [];
  for (let i = 0; i < 6; i++) seq.push([0, i, 4, 6 - i]);
  let step = 0;
  let last = 0;
  const tick = (ts: number): void => {
    if (!map.getLayer(ROUTE_LAYER)) return;
    tickUserPin(ts); // the user pin's ripple shares this rAF (v0.9.5c)
    if (ts - last > 90) {
      step = (step + 1) % seq.length;
      map.setPaintProperty(ROUTE_LAYER, "line-dasharray", seq[step] as number[]);
      last = ts;
    }
    animTimer = requestAnimationFrame(tick);
  };
  animTimer = requestAnimationFrame(tick);
}

function emptyFC(): GeoJSON.FeatureCollection {
  return { type: "FeatureCollection", features: [] };
}

/** Geodesic-ish circle polygon (64 vertices) around lon/lat with radius in km. */
function circle(lon: number, lat: number, km: number): GeoJSON.FeatureCollection {
  return { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [circleRing(lon, lat, km, 64)] } }] };
}

/** Circle ring, wound clockwise so it cuts a hole out of the mask rectangle. */
function circleRing(lon: number, lat: number, km: number, segments: number): [number, number][] {
  const pts: [number, number][] = [];
  const dLat = km / 110.574;
  const dLon = km / (111.32 * Math.cos((lat * Math.PI) / 180));
  for (let i = 0; i <= segments; i++) {
    const t = -(i / segments) * 2 * Math.PI; // clockwise
    pts.push([lon + dLon * Math.cos(t), lat + dLat * Math.sin(t)]);
  }
  return pts;
}

/** Route → one line per run of reaches sharing a name (same name rule as the cards); unnamed
 *  reaches end a run and get no label (no invented names). Lines follow the reaches' snap points. */
function routeNameLines(pathIds: number[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  let name: string | null = null, coords: [number, number][] = [];
  const flush = (): void => {
    if (name && coords.length >= 2) features.push({ type: "Feature", properties: { name }, geometry: { type: "LineString", coordinates: chaikin(coords) } });
  };
  for (const [id, c] of pathReachPoints(pathIds)) {
    const n = displayRiverName(id);
    if (n !== name) { flush(); const last = coords[coords.length - 1]; coords = n && last && name ? [last] : []; name = n; }
    coords.push(c);
  }
  flush();
  return { type: "FeatureCollection", features };
}

/** Two rounds of Chaikin corner-cutting — one snap point per reach makes a jagged line on which
 *  MapLibre can't fit a line label (text-max-angle); the label line only, the drawn route is unchanged. */
function chaikin(pts: [number, number][]): [number, number][] {
  let out = pts;
  for (let k = 0; k < 2 && out.length > 2; k++) {
    const next: [number, number][] = [out[0]!];
    for (let i = 0; i < out.length - 1; i++) {
      const [x0, y0] = out[i]!, [x1, y1] = out[i + 1]!;
      next.push([0.75 * x0 + 0.25 * x1, 0.75 * y0 + 0.25 * y1], [0.25 * x0 + 0.75 * x1, 0.25 * y0 + 0.75 * y1]);
    }
    next.push(out[out.length - 1]!);
    out = next;
  }
  return out;
}
